import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, AlertTriangle, Upload,
  Image as ImageIcon, Type, Link as LinkIcon, History,
  RotateCcw, X, Check, Eye, Sparkles, Pencil, Move
} from 'lucide-react';
import { Preview } from '@creatomate/preview';
import { contentService } from '../../services/ContentService';

const CREATOMATE_PUBLIC_TOKEN = process.env.REACT_APP_CREATOMATE_PUBLIC_TOKEN;

const CreatomateContentEditor = () => {
  const { batchId, contentId } = useParams();

  // Refs
  const previewContainerRef = useRef(null);
  const previewInstanceRef = useRef(null);
  const isMountedRef = useRef(true);
  const isInitializingRef = useRef(false);

  // Estados
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);

  // Dados
  const [content, setContent] = useState(null);
  const [renderScript, setRenderScript] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Campos editaveis extraidos do render_script
  const [textFields, setTextFields] = useState([]);
  const [imageFields, setImageFields] = useState([]);

  // Historico
  const [showHistory, setShowHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      destroyPreview();
    };
  }, []);

  const destroyPreview = useCallback(() => {
    const instance = previewInstanceRef.current;
    if (instance) {
      try {
        instance.dispose();
      } catch (e) {
        console.warn('[CreatomateEditor] Erro ao destruir preview:', e);
      }
      previewInstanceRef.current = null;
    }
  }, []);

  // Extrair campos editaveis dos elements do render_script
  const extractEditableFields = useCallback((source) => {
    const texts = [];
    const images = [];

    const walkElements = (elements, parentPath = '') => {
      if (!elements) return;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i];
        const path = parentPath ? `${parentPath}.${i}` : `${i}`;

        if (el.type === 'text' && el.text !== undefined) {
          texts.push({
            path,
            name: el.name || `Texto ${texts.length + 1}`,
            text: el.text,
            elementIndex: i,
            parentPath,
          });
        }

        if (el.type === 'image' && el.source !== undefined) {
          images.push({
            path,
            name: el.name || `Imagem ${images.length + 1}`,
            source: el.source,
            elementIndex: i,
            parentPath,
          });
        }

        if (el.elements) {
          walkElements(el.elements, path + '.elements');
        }
      }
    };

    walkElements(source.elements);
    return { texts, images };
  }, []);

  // Carregar dados do content
  useEffect(() => {
    loadContentData();
  }, [batchId, contentId]);

  const loadContentData = async () => {
    if (!isMountedRef.current) return;
    setIsLoading(true);
    setError(null);

    try {
      const batchResponse = await contentService.getBatch(batchId);
      if (!isMountedRef.current) return;

      const batchData = batchResponse.data;
      const contentData = batchData.contents?.find(c => c.id === contentId);

      if (!contentData) throw new Error('Conteúdo não encontrado');
      if (contentData.model !== 'creatomate') throw new Error('Edição disponível apenas para conteúdos Creatomate');
      if (contentData.status !== 'completed') throw new Error('Conteúdo ainda não foi processado');

      const script = contentData.metadata?.render_script;
      if (!script) throw new Error('Dados de edição (render_script) não encontrados');

      setContent(contentData);
      setRenderScript(script);
      setEditHistory(contentData.edit_history || []);

      const { texts, images } = extractEditableFields(script);
      setTextFields(texts);
      setImageFields(images);
    } catch (err) {
      if (isMountedRef.current) setError(err.message || 'Erro ao carregar');
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  };

  // Inicializar Preview SDK
  useEffect(() => {
    if (
      renderScript &&
      previewContainerRef.current &&
      !previewInstanceRef.current &&
      !isInitializingRef.current &&
      CREATOMATE_PUBLIC_TOKEN
    ) {
      initializePreview();
    }

    return () => {
      destroyPreview();
      if (isMountedRef.current) setPreviewReady(false);
    };
  }, [renderScript, destroyPreview]);

  const initializePreview = async () => {
    if (isInitializingRef.current || previewInstanceRef.current) return;
    isInitializingRef.current = true;

    try {
      const container = previewContainerRef.current;
      if (!container) return;
      container.innerHTML = '';

      const preview = new Preview(container, 'interactive', CREATOMATE_PUBLIC_TOKEN);
      previewInstanceRef.current = preview;

      preview.onReady = async () => {
        if (!isMountedRef.current) return;
        try {
          await preview.setSource(renderScript);
          if (isMountedRef.current) setPreviewReady(true);
        } catch (err) {
          console.error('[CreatomateEditor] Erro ao carregar source:', err);
          if (isMountedRef.current) setError('Erro ao carregar preview: ' + err.message);
        }
      };

      preview.onError = (err) => {
        console.error('[CreatomateEditor] Preview error:', err);
        if (isMountedRef.current) setError('Erro no preview: ' + (err?.message || 'desconhecido'));
      };

      preview.onStateChange = () => {
        if (isMountedRef.current) setHasChanges(true);
      };
    } catch (err) {
      if (isMountedRef.current) setError('Erro ao inicializar editor: ' + err.message);
    } finally {
      isInitializingRef.current = false;
    }
  };

  // Atualizar texto no preview
  const handleTextChange = async (field, value) => {
    setTextFields(prev =>
      prev.map(f => f.path === field.path ? { ...f, text: value } : f)
    );
    setHasChanges(true);

    const preview = previewInstanceRef.current;
    if (preview && previewReady) {
      try {
        const source = await preview.getSource();
        const elements = source.elements;
        const indices = field.path.split('.').filter(p => p !== 'elements').map(Number);

        let target = { elements };
        for (let i = 0; i < indices.length - 1; i++) {
          target = target.elements[indices[i]];
        }
        target.elements[indices[indices.length - 1]].text = value;

        await preview.setSource(source);
      } catch (err) {
        console.warn('[CreatomateEditor] Erro ao atualizar texto no preview:', err);
      }
    }
  };

  // Atualizar imagem no preview
  const handleImageUrlChange = async (field, url) => {
    setImageFields(prev =>
      prev.map(f => f.path === field.path ? { ...f, source: url } : f)
    );
    setHasChanges(true);

    const preview = previewInstanceRef.current;
    if (preview && previewReady) {
      try {
        const source = await preview.getSource();
        const elements = source.elements;
        const indices = field.path.split('.').filter(p => p !== 'elements').map(Number);

        let target = { elements };
        for (let i = 0; i < indices.length - 1; i++) {
          target = target.elements[indices[i]];
        }
        target.elements[indices[indices.length - 1]].source = url;

        await preview.setSource(source);
      } catch (err) {
        console.warn('[CreatomateEditor] Erro ao atualizar imagem no preview:', err);
      }
    }
  };

  // Upload de imagem
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (field, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Use: JPEG, PNG, WebP ou GIF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Máximo: 10MB');
      return;
    }

    setIsUploading(true);
    try {
      const response = await contentService.uploadEditImage(contentId, file);
      if (!isMountedRef.current) return;
      const imageUrl = response.url || response.data?.url;
      if (imageUrl) handleImageUrlChange(field, imageUrl);
    } catch (err) {
      if (isMountedRef.current) alert('Erro no upload');
    } finally {
      if (isMountedRef.current) setIsUploading(false);
    }
  };

  // Salvar
  const handleSave = async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const preview = previewInstanceRef.current;
      let sourceToSave = renderScript;

      if (preview && previewReady) {
        sourceToSave = await preview.getSource();
      }

      await contentService.saveCreatomateEdit(batchId, contentId, {
        source: sourceToSave,
        regenerate: true,
      });

      if (!isMountedRef.current) return;

      setHasChanges(false);
      setSaveSuccess(true);
      await loadContentData();
      setTimeout(() => {
        if (isMountedRef.current) setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      if (isMountedRef.current) {
        alert('Erro ao salvar: ' + (err.message || 'Tente novamente'));
      }
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  // Reverter historico
  const handleRevert = async (historyIndex) => {
    if (!window.confirm('Reverter para esta versão?')) return;
    setIsSaving(true);
    try {
      await contentService.restoreContentVersion(batchId, contentId, historyIndex);
      if (!isMountedRef.current) return;
      await loadContentData();
      setShowHistory(false);
    } catch (err) {
      if (isMountedRef.current) alert('Erro ao reverter');
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const formatFieldName = (name) => {
    return name
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Loading
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Carregando editor...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erro</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-all"
            >
              Recarregar
            </button>
            <Link
              to={`/contents/batch/${batchId}`}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
            >
              Voltar
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/contents/batch/${batchId}`}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-amber-500" />
                  Editar Post
                </h1>
                <p className="text-sm text-gray-500">
                  {content?.metadata?.layout_agent_name || content?.metadata?.layout_type || 'Creatomate'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {editHistory.length > 0 && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Histórico"
                >
                  <History className="w-5 h-5" />
                </button>
              )}

              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                  hasChanges && !isSaving
                    ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : saveSuccess ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Salvando...' : saveSuccess ? 'Salvo!' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Conteudo */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Preview - 3 colunas */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden sticky top-20">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Preview Interativo</span>
                </div>
                <div className="flex items-center gap-2">
                  {previewReady && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                      ● Ao vivo
                    </span>
                  )}
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Move className="w-3 h-3" /> Arraste para mover
                  </span>
                </div>
              </div>

              <div className="p-4">
                <div className="relative bg-gray-100 rounded-xl overflow-hidden">
                  <div
                    ref={previewContainerRef}
                    className="w-full"
                    style={{ aspectRatio: '1/1', minHeight: '300px' }}
                  />

                  {!previewReady && (
                    <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Preparando editor interativo...</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Formulario - 2 colunas */}
          <div className="lg:col-span-2 space-y-4">

            {/* Textos */}
            {textFields.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <Type className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Textos</span>
                </div>

                <div className="p-5 space-y-4">
                  {textFields.map((field) => (
                    <div key={field.path}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formatFieldName(field.name)}
                      </label>
                      <textarea
                        value={field.text || ''}
                        onChange={(e) => handleTextChange(field, e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all resize-none text-gray-900"
                        rows={field.name.toLowerCase().includes('title') ? 2 : 1}
                        placeholder="Digite aqui..."
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Imagens */}
            {imageFields.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Imagens</span>
                </div>

                <div className="p-5 space-y-5">
                  {imageFields.map((field) => (
                    <div key={field.path}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formatFieldName(field.name)}
                      </label>

                      <div className="flex gap-3">
                        {field.source && (
                          <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                            <img
                              src={field.source}
                              alt={field.name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}

                        <div className="flex-1 flex gap-2">
                          <div className="flex-1 relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="url"
                              value={field.source || ''}
                              onChange={(e) => handleImageUrlChange(field, e.target.value)}
                              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm"
                              placeholder="URL da imagem..."
                            />
                          </div>

                          <button
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/jpeg,image/png,image/webp,image/gif';
                              input.onchange = (e) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageUpload(field, file);
                              };
                              input.click();
                            }}
                            disabled={isUploading}
                            className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all flex-shrink-0"
                          >
                            {isUploading ? (
                              <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                            ) : (
                              <Upload className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dica */}
            <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Edite os campos ao lado ou arraste os elementos diretamente no preview. Clique em <strong>Salvar</strong> para gerar a imagem final.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Modal Historico */}
      {showHistory && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowHistory(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-amber-500" />
                Histórico
              </h2>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {editHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum histórico</p>
              ) : (
                <div className="space-y-3">
                  {editHistory.map((entry, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl"
                    >
                      {entry.previous_url && (
                        <img
                          src={entry.previous_url}
                          alt={`Versão ${index + 1}`}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                      )}

                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">
                          Versão {editHistory.length - index}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.edited_at).toLocaleString('pt-BR')}
                        </p>
                      </div>

                      <button
                        onClick={() => handleRevert(index)}
                        disabled={isSaving}
                        className="px-3 py-2 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-1.5"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatomateContentEditor;
